/**
 * Monaco Editor 语言工具函数
 *
 * 提供语言检测、动态加载、扩展名映射等功能
 */

import * as monaco from 'monaco-editor'
import { SUPPORTED_LANGUAGES, WORKER_LANGUAGES } from '@/constants/monaco-config'
import type { LanguageInfo, SupportedLanguage } from '@/types/monaco-editor'

/**
 * 支持的语言数组
 */
export const supportedLanguages = SUPPORTED_LANGUAGES

/**
 * 检测语言类型
 */
export const detectLanguage = (content: string, filename?: string): SupportedLanguage => {
  // 1. 基于文件名扩展名检测
  if (filename) {
    const extension = filename.split('.').pop()?.toLowerCase()
    const languageByExtension = supportedLanguages.find(lang => {
      return lang.extension === extension || (extension && lang.aliases?.includes(extension))
    })
    if (languageByExtension) {
      return languageByExtension.id
    }
  }

  // 2. 基于内容特征检测
  const contentLower = content.toLowerCase().trim()

  // TypeScript/JavaScript 检测
  if (
    contentLower.includes('interface ') ||
    contentLower.includes(': string') ||
    contentLower.includes(': number') ||
    contentLower.includes('export type')
  ) {
    return 'typescript'
  }

  if (
    contentLower.includes('function ') ||
    contentLower.includes('const ') ||
    contentLower.includes('let ') ||
    contentLower.includes('var ')
  ) {
    return 'javascript'
  }

  // Python 检测
  if (
    contentLower.includes('def ') ||
    contentLower.includes('import ') ||
    contentLower.includes('from ') ||
    contentLower.includes('print(')
  ) {
    return 'python'
  }

  // Java 检测
  if (contentLower.includes('public class ') || contentLower.includes('private ') || contentLower.includes('System.out.println')) {
    return 'java'
  }

  // HTML 检测
  if (
    contentLower.includes('<!doctype html') ||
    contentLower.includes('<html') ||
    contentLower.includes('<div') ||
    contentLower.includes('<body')
  ) {
    return 'html'
  }

  // CSS 检测
  if (
    contentLower.includes('{') &&
    contentLower.includes('}') &&
    (contentLower.includes('color:') ||
      contentLower.includes('background:') ||
      contentLower.includes('margin:') ||
      contentLower.includes('padding:'))
  ) {
    return 'css'
  }

  // JSON 检测
  if ((contentLower.startsWith('{') && contentLower.endsWith('}')) || (contentLower.startsWith('[') && contentLower.endsWith(']'))) {
    try {
      JSON.parse(content)
      return 'json'
    } catch {
      // 不是有效的JSON，继续检测其他语言
    }
  }

  // SQL 检测
  if (
    contentLower.includes('select ') ||
    contentLower.includes('insert into ') ||
    contentLower.includes('update ') ||
    contentLower.includes('delete from ')
  ) {
    return 'sql'
  }

  // Markdown 检测
  if (contentLower.includes('# ') || contentLower.includes('## ') || contentLower.includes('**') || contentLower.includes('* ')) {
    return 'markdown'
  }

  // YAML 检测
  if (contentLower.includes(': ') && !contentLower.includes('var ') && !contentLower.includes('let ')) {
    return 'yaml'
  }

  // XML 检测
  if (contentLower.includes('<') && contentLower.includes('>') && contentLower.includes('</')) {
    return 'xml'
  }

  // Go 检测
  if (contentLower.includes('package ') || contentLower.includes('func ') || contentLower.includes('import (')) {
    return 'go'
  }

  // Rust 检测
  if (
    contentLower.includes('fn ') ||
    contentLower.includes('use ') ||
    contentLower.includes('let mut') ||
    contentLower.includes('pub fn')
  ) {
    return 'rust'
  }

  // Dockerfile 检测
  if (contentLower.includes('from ') || contentLower.includes('run ') || contentLower.includes('copy ') || contentLower.includes('cmd ')) {
    return 'dockerfile'
  }

  // Bash 检测
  if (
    contentLower.includes('#!/bin/bash') ||
    contentLower.includes('echo ') ||
    contentLower.includes('export ') ||
    contentLower.includes('chmod ')
  ) {
    return 'bash'
  }

  return 'typescript' // 默认返回 TypeScript
}

/**
 * 获取语言的文件扩展名
 */
export const getLanguageExtension = (language: SupportedLanguage): string => {
  const langInfo = supportedLanguages.find(lang => lang.id === language)
  return langInfo?.extension || 'txt'
}

/**
 * 获取语言信息
 */
export const getLanguageInfo = (language: SupportedLanguage): LanguageInfo | undefined => {
  return supportedLanguages.find(lang => lang.id === language)
}

/**
 * 根据文件扩展名获取语言
 */
export const getLanguageByExtension = (extension: string): SupportedLanguage => {
  const langInfo = supportedLanguages.find(lang => lang.extension === extension || lang.aliases?.includes(extension.toLowerCase()))
  return langInfo?.id || 'typescript'
}

/**
 * 检查语言是否需要Worker支持
 */
export const isWorkerSupportedLanguage = (language: SupportedLanguage): boolean => {
  return WORKER_LANGUAGES.includes(language as (typeof WORKER_LANGUAGES)[number])
}

/**
 * 动态加载语言支持
 */
export const loadLanguageSupport = async (language: SupportedLanguage): Promise<void> => {
  try {
    switch (language) {
      case 'typescript':
      case 'javascript':
        // TypeScript/JavaScript 内置支持
        break

      case 'html':
        // HTML 内置支持，不需要额外导入
        break

      case 'css':
        // CSS 内置支持，不需要额外导入
        break

      case 'json':
        // JSON 内置支持，不需要额外导入
        break

      case 'markdown':
        // Markdown 内置支持，不需要额外导入
        break

      case 'yaml':
        // YAML uses basic syntax highlighting
        console.debug('Using basic YAML syntax highlighting')
        break

      case 'python':
        // Python 使用基本语法高亮，不需要特殊导入
        console.debug('Using basic Python syntax highlighting')
        break

      case 'sql':
        // SQL 使用基本语法高亮
        console.debug('Using basic SQL syntax highlighting')
        break

      case 'xml':
        // XML 内置支持，不需要额外导入
        break

      case 'graphql':
        // GraphQL uses basic syntax highlighting
        console.debug('Using basic GraphQL syntax highlighting')
        break

      default:
        console.warn(`Language ${language} may not have full support, using basic highlighting`)
    }
  } catch (error) {
    console.error(`Failed to load language support for ${language}:`, error)
    // 不抛出错误，继续使用基础高亮
  }
}

/**
 * 预加载常用语言
 */
export const preloadCommonLanguages = async (): Promise<void> => {
  const commonLanguages: SupportedLanguage[] = ['typescript', 'javascript', 'html', 'css', 'json', 'markdown', 'python']

  console.debug('Preloading common language support...')

  const loadPromises = commonLanguages.map(language =>
    loadLanguageSupport(language).catch(error => console.warn(`Failed to preload ${language}:`, error)),
  )

  await Promise.allSettled(loadPromises)
  console.debug('Common language preloading completed')
}

/**
 * 设置Monaco Editor的语言配置
 */
export const setupMonacoLanguages = (): void => {
  // 注册自定义语言（如果需要）

  // 设置自动缩进规则
  monaco.editor.setModelOptions(monaco.editor.createModel('', 'typescript'), {
    indentSize: 2,
    tabSize: 2,
    insertSpaces: true,
  })

  // 配置语言检测规则
  monaco.languages.register({ id: 'typescript' })
  monaco.languages.setLanguageConfiguration('typescript', {
    comments: {
      blockComment: ['/*', '*/'],
      lineComment: '//',
    },
    brackets: [
      ['{', '}'],
      ['[', ']'],
      ['(', ')'],
    ],
    autoClosingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"', notIn: ['string'] },
      { open: "'", close: "'", notIn: ['string', 'comment'] },
    ],
  })
}

/**
 * 获取语言显示名称
 */
export const getLanguageDisplayName = (language: SupportedLanguage): string => {
  const langInfo = getLanguageInfo(language)
  return langInfo?.name || language.charAt(0).toUpperCase() + language.slice(1)
}

/**
 * 搜索语言
 */
export const searchLanguages = (query: string): LanguageInfo[] => {
  const lowerQuery = query.toLowerCase()
  return supportedLanguages.filter(
    lang =>
      lang.name.toLowerCase().includes(lowerQuery) ||
      lang.id.toLowerCase().includes(lowerQuery) ||
      lang.aliases?.some(alias => alias.toLowerCase().includes(lowerQuery)),
  )
}
